import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Observable, Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'app-parties-currently-leading-chart',
  templateUrl: './parties-currently-leading-chart.component.html',
  styleUrls: ['./parties-currently-leading-chart.component.scss']
})
export class PartiesCurrentlyLeadingChartComponent implements AfterViewInit, OnDestroy {
  @Input() chartTitle!: string;
  @Input() chartSubTitle!: string;
  public resizeObservable$: Observable<Event> | undefined = undefined;
  public resizeSubscription$: Subscription | undefined = undefined;

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public firstPreferenceVotes: IReportFirstPreference[] = [];

  public chart: any = null;
  public updateFlag = false;
  public isHighcharts = typeof Highcharts === 'object';
  public Highcharts: typeof Highcharts = Highcharts;
  public chartConstructor = 'chart';
  public chartCallback: any = null;
  public graphData: Highcharts.PointOptionsObject[] = [];
  public startAngle = 0;
  public hasData = true;

  chartOptions: Highcharts.Options = {
    series: [{
      data: this.graphData,
      type: 'pie',
      startAngle: 20,
      endAngle: 20,
    }],
    title: { text: undefined }, // 'Votes won (first preferences)' },
    chart: { height: 250, margin: 0, /*backgroundColor: 'rgb(243 243 243 / 50%)'*/ },
    legend: { align: 'right', verticalAlign: 'middle', layout: 'vertical' },
    credits: { enabled: false }, // remove watermark
    tooltip: {
      valueSuffix: '<br>({point.percentage:.1f} %)'
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        cursor: 'pointer',
        dataLabels: { enabled: true, format: '<b>{point.name}</b>:<br><span style="color:{point.color}">{point.percentage:.1f} %</span>'},
        showInLegend: false, // disable legend
      }
    },
    accessibility: {
      enabled: true,
      typeDescription: this.chartTitle,
      description: this.chartSubTitle,
    },
    lang: {
      noData: 'no party leading'
    },
    exporting: {
      enabled: false,
      buttons: {
        printButton: {
          enabled: false
        },
        exportButton: {
            enabled: true
        }
      },
      showTable: false
    }
  };

  constructor(private electionService: ElectionService) {
    const self = this;
    this.chartCallback = (chart: any) => {
      self.chart = chart;
    };

    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }
  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private loadChart(page: ISelectPage): void {
    this.firstPreferenceVotes = [];
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    if (page.pageType === PageTypeEnum.CountHADistrict) {
      const district = election?.electionCount?.getDistrict(page.countDistrict);
      this.firstPreferenceVotes = district?.candidates.filter(x => x.totalPollingPlaceTwoCandidatePrefVotes > 0).map(candidate =>
        {
          return {
            district: district.districtName,
            partyId: candidate.partyId,
            party: candidate.partyName,
            colourCode: candidate.colourCode,
            candidateName: candidate.candidateName,
            votes: candidate.totalPollingPlaceTwoCandidatePrefVotes ?? 0,
            share: 0,
            change: 0
          };
        }) ?? [];
      this.hasData = this.firstPreferenceVotes.length > 0;
      this.updateChart();
    }
  }


  ngOnDestroy() {
  //  this.resizeSubscription$?.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  private resizeChart(windowSize: any) {
    let newChartWidth = (windowSize * 30) / 100;
    if (windowSize < 960) {
      newChartWidth = windowSize - 302;
    }
    if (windowSize < 800) {
      newChartWidth = windowSize - 63;
    } // compare this with the css value. small media size
    this.chartOptions.chart = { width: newChartWidth };
    this.updateFlag = true;
  }


  private updateChart(width?: number) {
    const self = this;
    const chart = this.chart;
    chart.showLoading();
  //  setTimeout(() => {
    chart.hideLoading();
    this.convertToGraphData();
    self.chartOptions.series = [{
      data: this.graphData,
      type: 'pie',
      name: 'votes'
    }];
    self.chartOptions.plotOptions = {
      pie: {
        size: null, // '80%',
        startAngle: this.startAngle,
        center: ['50%', '45%'],
        innerSize: '75%'
      }
    };
    // this.chartOptions.chart?.width = !!width ? width : this.chartOptions.chart?.width;
    self.updateFlag = true;
 //   }, 1000);
  }

  private convertToGraphData() {
    this.graphData = [];
    this.firstPreferenceVotes.forEach(v => {
      this.graphData.push({
        name: `${v.candidateName} (${v.partyId})` ?? v.party,
        y: v.votes,
        color: v.colourCode
      });
    });
    const x = Math.max(...this.firstPreferenceVotes.map(o => o.share), 0);
    this.startAngle = 180 - (x * 360);
  }

}
